import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dataGridCustomizations } from "../../../theme/customizations";
import AppTheme from "../../../theme/AppTheme";
import {
  CssBaseline,
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import PaginationButtons from "../../../components/Pagination/PaginationButtons";
import {
  notifyError,
  notifySuccess,
} from "../../../components/ToastContainer/Toast/Toast";
import {
  getOffers,
  publishOffer,
  unpublishOffer,
} from "../../../service/BuyerConfiguration/OfferManagement/offerService";

const STATUS_COLOR: Record<string, "default" | "success" | "warning" | "error" | "info"> = {
  draft: "default",
  scheduled: "info",
  booking_open: "success",
  booking_closed: "warning",
  sold_out: "warning",
  preparing: "info",
  out_for_delivery: "info",
  completed: "default",
  cancelled: "error",
  expired: "default",
};

/**
 * Statuses in which the edit action is offered. Live offers (booking_open,
 * sold_out) are included, but the API restricts those edits to safe fields —
 * name, images and increasing slots. Price, timing and payment terms are
 * frozen once someone has booked, so a customer's accepted deal cannot change
 * underneath them.
 */
const EDITABLE_STATUSES = ["draft", "scheduled", "booking_open", "sold_out"];

const OfferManagement = (props: { disableCustomTheme?: boolean }) => {
  const xThemeComponents = { ...dataGridCustomizations };
  const navigate = useNavigate();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getOffers({ page, limit });
      const payload = res?.data ?? res;
      setRows(payload?.data ?? []);
      setTotal(payload?.total ?? 0);
    } catch (e: any) {
      notifyError(e?.message || "Failed to load offers");
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  const handleTogglePublish = async (row: any) => {
    try {
      if (row.is_published) {
        await unpublishOffer(row.id);
        notifySuccess(`"${row.name}" hidden from the buyer app`);
      } else {
        await publishOffer(row.id);
        notifySuccess(`"${row.name}" is now live in the buyer app`);
      }
      fetchOffers();
    } catch (e: any) {
      notifyError(e?.message || "Could not change publish state");
    }
  };

  const columns: GridColDef[] = [
    { field: "name", headerName: "Offer Name", flex: 1.6, minWidth: 200 },
    { field: "store_id", headerName: "Store", width: 90 },
    {
      field: "offer_price",
      headerName: "Price",
      width: 120,
      renderCell: (p) => (
        <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
          <strong>₹{Number(p.row.offer_price)}</strong>
          {p.row.strike_price ? (
            <span style={{ textDecoration: "line-through", opacity: 0.55 }}>
              ₹{Number(p.row.strike_price)}
            </span>
          ) : null}
        </Box>
      ),
    },
    {
      field: "fulfilment_mode",
      headerName: "Type",
      width: 130,
      renderCell: (p) =>
        p.row.fulfilment_mode === "prebooking" ? "Pre-booking" : "Direct",
    },
    {
      field: "window",
      headerName: "Booking Window",
      flex: 1.2,
      minWidth: 200,
      renderCell: (p) =>
        p.row.booking_opens_at
          ? `${new Date(p.row.booking_opens_at).toLocaleString()} → ${new Date(
              p.row.booking_closes_at,
            ).toLocaleTimeString()}`
          : "—",
    },
    {
      field: "claims",
      headerName: "Claimed",
      width: 120,
      renderCell: (p) => {
        // Count confirmed bookings only. Deriving this from slots_remaining
        // counted unpaid holds as claims, so a customer merely opening
        // checkout made the offer look booked. Holds are shown separately.
        const confirmed = p.row.confirmed_count ?? p.row.claims_count ?? 0;
        const holding = p.row.holding_count ?? 0;
        return (
          <Box sx={{ lineHeight: 1.3 }}>
            <span>
              {confirmed} / {p.row.max_claims}
            </span>
            {holding > 0 && (
              <Box component="span" sx={{ display: "block", fontSize: 11, opacity: 0.7 }}>
                +{holding} holding
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (p) => (
        <Chip
          size="small"
          label={String(p.row.status).replace(/_/g, " ")}
          color={STATUS_COLOR[p.row.status] ?? "default"}
        />
      ),
    },
    {
      field: "is_published",
      headerName: "In App",
      width: 100,
      renderCell: (p) => (
        <Chip
          size="small"
          label={p.row.is_published ? "Live" : "Hidden"}
          color={p.row.is_published ? "success" : "default"}
          variant={p.row.is_published ? "filled" : "outlined"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (p) => (
        <Box>
          <Tooltip title="View">
            <IconButton
              size="small"
              onClick={() => navigate(`/offer-management/${p.row.id}`)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              EDITABLE_STATUSES.includes(p.row.status)
                ? "Edit"
                : "Completed, cancelled or expired offers cannot be edited"
            }
          >
            <span>
              <IconButton
                size="small"
                disabled={!EDITABLE_STATUSES.includes(p.row.status)}
                onClick={() => navigate(`/offer-management/${p.row.id}/edit`)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={p.row.is_published ? "Hide from buyer app" : "Sync to buyer app"}
          >
            <IconButton size="small" onClick={() => handleTogglePublish(p.row)}>
              {p.row.is_published ? (
                <CloudOffIcon fontSize="small" />
              ) : (
                <CloudUploadIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ width: "100%", p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h5">Offers</Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Combo offers with limited slots. Separate from promo codes.
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => navigate("/offer-management/create")}
          >
            + Create Offer
          </Button>
        </Box>

        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={(r) => r.id}
          autoHeight
          hideFooter
          disableRowSelectionOnClick
        />

        <PaginationButtons
          count={Math.max(1, Math.ceil(total / limit))}
          page={page}
          onChange={(value: number) => setPage(value)}
          totalItems={total}
          itemsPerPage={limit}
        />
      </Box>
    </AppTheme>
  );
};

export default OfferManagement;
